/** API payloads → the timeline's model. Snake→camel plus the sparse contract.
 *
 *  Kept separate from `buildTimeline` so the builder never sees an API shape:
 *  the AI-proposal path will have its own adapter (`sparse.ts`) feeding the
 *  same builder.
 */
import type {
  ApiEventCatalogEntry,
  ApiEventPosition,
  ApiTimeline,
  ApiTimelineRow,
  ApiTimelineStats,
} from '@/api/notificationTimeline';

import type { CatalogEntry } from './buildTimeline';
import type {
  CannotFireReason,
  EventPosition,
  TimelineNotification,
  TimelineStats,
  WarningCode,
} from './types';

const WARNING_CODES: ReadonlySet<string> = new Set<WarningCode>([
  'disabled',
  'no_delivery_methods',
  'no_enabled_delivery_methods',
  'suppressed_by_child',
  'event_cannot_fire',
  'unknown_event_code',
  'never_fired',
]);

const CANNOT_FIRE_REASONS: ReadonlySet<string> = new Set<CannotFireReason>([
  'aliased',
  'not_emitted',
  'unresolvable',
]);

export function adaptPosition(p: ApiEventPosition): EventPosition {
  return {
    phase: p.phase as EventPosition['phase'],
    band: p.band,
    anchor: p.anchor as EventPosition['anchor'],
    direction: p.direction as EventPosition['direction'],
    offsetSeconds: p.offset_seconds,
    offsetConfigurable: p.offset_configurable,
    family: p.family,
    evid: p.evid,
    labelShort: p.label_short,
    labelLong: p.label_long,
    sortKey: p.sort_key,
  };
}

function adaptReason(raw: string | null): CannotFireReason | null {
  return raw && CANNOT_FIRE_REASONS.has(raw) ? (raw as CannotFireReason) : null;
}

export function adaptNotification(row: ApiTimelineRow): TimelineNotification {
  return {
    key: `nf:${row.id}`,
    id: row.id,
    name: row.name,
    event: row.event,
    enabled: row.enabled,
    origin: row.origin,
    materialized: true,
    tenant: row.tenant,
    tourOverrideMode: row.tour_override_mode,
    suppressedByChild: row.suppressed_by_child,
    audience: row.audience,
    tours: row.tours,
    languagePolicy: row.language_policy,
    blockRefCount: row.block_ref_count,
    disableDefaultBlocks: row.disable_default_blocks,
    deliveryMethodOrdering: row.delivery_method_ordering,
    timedeltaSeconds: row.timedelta_seconds,
    ruleSummary: {
      matchType: row.rule_summary.match_type,
      count: row.rule_summary.count,
      rulesets: row.rule_summary.rulesets,
    },
    deliveryMethods: row.delivery_methods.map((dm) => ({
      id: dm.id,
      method: dm.method,
      methodLabel: dm.method_label,
      enabled: dm.enabled,
      sequence: dm.sequence,
      renderingType: dm.rendering_type,
      themeCodename: dm.theme_codename,
    })),
    position: adaptPosition(row.position),
    canFire: row.can_fire,
    cannotFireReason: adaptReason(row.cannot_fire_reason),
    canonicalEvent: row.canonical_event,
    // Unknown codes are dropped rather than widening WarningCode: a badge we
    // have no copy for would render as a raw enum string to an operator.
    warnings: row.warnings.filter((w): w is WarningCode => WARNING_CODES.has(w)),
    updatedTs: row.updated_ts,
  };
}

export function adaptTimeline(payload: ApiTimeline): {
  notifications: TimelineNotification[];
  tenant: ApiTimeline['tenant'];
  generatedTs: string;
  statsAvailable: boolean;
} {
  return {
    notifications: payload.notifications.map(adaptNotification),
    tenant: payload.tenant,
    generatedTs: payload.generated_ts,
    statsAvailable: payload.stats_available,
  };
}

export function adaptCatalog(entries: ApiEventCatalogEntry[]): CatalogEntry[] {
  return entries.map((e) => ({
    code: e.code,
    family: e.family,
    label: e.label,
    position: adaptPosition(e.position),
    canFire: e.can_fire,
    cannotFireReason: adaptReason(e.cannot_fire_reason),
    canonicalCode: e.canonical_code,
  }));
}

export function adaptStats(payload: ApiTimelineStats): TimelineStats {
  return {
    available: payload.available,
    reason: payload.reason,
    windowDays: payload.window_days,
    scope: payload.scope,
    byNotification: Object.fromEntries(
      Object.entries(payload.by_notification).map(([id, s]) => [
        id,
        { fires: s.fires, errors: s.errors, lastTs: s.last_ts },
      ]),
    ),
  };
}

/** Add `never_fired` to rows the stats say have not fired in the window.
 *
 *  Only when stats are actually available: in a logging mode that writes no
 *  notification logs every row would look dead, which is a different and false
 *  claim than "we don't know".
 */
export function applyStatsWarnings(
  notifications: TimelineNotification[],
  stats: TimelineStats | undefined,
): TimelineNotification[] {
  if (!stats?.available) return notifications;
  return notifications.map((n) => {
    if (n.id === null) return n;
    const fires = stats.byNotification[String(n.id)]?.fires ?? 0;
    if (fires > 0 || n.warnings.includes('never_fired')) return n;
    // A disabled notification not firing is not news — don't double-badge it.
    if (n.enabled === false) return n;
    return { ...n, warnings: [...n.warnings, 'never_fired' as WarningCode] };
  });
}
